import { useState } from "react";
import { getContractorOnboardingLink, IS_STRIPE_CONFIGURED } from "../utils/stripeService";
import S from "../styles/styles";
import { Field, EmptyState } from "./UI";
import { TRADES } from "../data/seed";

const EMPTY_FORM = { name: "", businessName: "", trade: "Plumbing", email: "", phone: "", gstNumber: "", address: "", city: "", province: "BC", postalCode: "", notes: "", paymentMethod: "etransfer" };

const ContractorsTab = ({ contractors, tickets, payments, onAddContractor }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [copied, setCopied]     = useState(null);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleAdd = () => {
    if (!form.name || !form.trade) return;
    onAddContractor({ ...form, companyId: 1, totalPaid: 0 });
    setForm(EMPTY_FORM);
    setShowForm(false);
  };

  const handleCopyEmail = (email, id) => {
    navigator.clipboard.writeText(email);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleETransfer = (contractor) => {
    // Opens default mail client with pre-filled e-transfer instructions
    const subject = encodeURIComponent("Payment from PropDesk");
    const body = encodeURIComponent(
      `Hi ${contractor.name},\n\nPlease find your payment via Interac e-Transfer.\n\nThank you for your work!\n\nPropDesk Property Management`
    );
    window.open(`mailto:${contractor.email}?subject=${subject}&body=${body}`);
  };

  const handleStripeOnboard = async (contractor) => {
    const result = await getContractorOnboardingLink(contractor.id, contractor.email);
    if (result.success && result.url) {
      window.open(result.url, "_blank");
    } else {
      alert("Could not generate Stripe onboarding link. Check Stripe configuration.");
    }
  };

  const tradeIcon = (trade) => ({
    Plumbing: "🔧", Electrical: "⚡", HVAC: "❄️",
    Appliance: "🔌", Carpentry: "🪚", Painting: "🎨",
    "Pest Control": "🐛", General: "🏠", Other: "🔨",
  }[trade] || "🔨");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
        <div style={S.pageTitle}>Contractors</div>
        <button style={{ ...S.btnPrimary, fontSize: 13, padding: "10px 18px" }} onClick={() => setShowForm(true)}>
          + Add Contractor
        </button>
      </div>
      <div style={S.pageSub}>{contractors.length} contractors in your network</div>

      {/* Summary strip */}
      <div style={{ display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap" }}>
        {[
          { label: "Total Contractors", value: contractors.length, color: "#F0EDE8" },
          { label: "Total Paid Out",    value: `$${contractors.reduce((s, c) => s + (c.totalPaid || 0), 0).toLocaleString()}`, color: "#E8622A" },
          { label: "Active Jobs",       value: tickets.filter((t) => t.contractorId && t.status !== "RESOLVED").length, color: "#2A7BE8" },
          { label: "Completed Jobs",    value: tickets.filter((t) => t.contractorId && t.status === "RESOLVED").length, color: "#22AA55" },
        ].map((st) => (
          <div key={st.label} style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 12, padding: "14px 18px", minWidth: 140 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: st.color }}>{st.value}</div>
            <div style={{ fontSize: 11, color: "#9993A8", fontWeight: 600, marginTop: 2 }}>{st.label}</div>
          </div>
        ))}
      </div>

      {/* Contractor cards */}
      {contractors.length === 0
        ? <EmptyState icon="👷" title="No contractors yet" subtitle="Add your first contractor above" />
        : <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 16 }}>
            {contractors.map((c) => {
              const assigned = tickets.filter((t) => t.contractorId === c.id);
              const active   = assigned.filter((t) => t.status !== "RESOLVED").length;
              const paid     = payments.filter((p) => p.contractorId === c.id && p.status === "paid")
                                       .reduce((s, p) => s + p.amount, 0);
              return (
                <div key={c.id} style={{ ...S.propCard, cursor: "default" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: "#E8622A18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>
                      {tradeIcon(c.trade)}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{c.name}</div>
                      <div style={{ fontSize: 12, color: "#9993A8" }}>{c.trade}</div>
                    </div>
                    {/* Payment method chip */}
                    <div style={{ marginLeft: "auto" }}>
                      {c.stripeOnboarded
                        ? <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 99, background: "#635BFF18", color: "#635BFF", border: "1px solid #635BFF44" }}>Stripe</span>
                        : <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 99, background: "#22AA5518", color: "#22AA55", border: "1px solid #22AA5544" }}>e-Transfer</span>
                      }
                    </div>
                  </div>

                  {/* Contact */}
                  <div style={{ fontSize: 12, color: "#9993A8", marginBottom: 4 }}>📧 {c.email || "—"}</div>
                  <div style={{ fontSize: 12, color: "#9993A8", marginBottom: 14 }}>📞 {c.phone || "—"}</div>

                  {/* Stats */}
                  <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                    <div style={S.propMetaChip}>{assigned.length} jobs total</div>
                    {active > 0 && <div style={{ ...S.propMetaChip, background: "#2A7BE818", color: "#2A7BE8" }}>{active} active</div>}
                    {paid > 0   && <div style={{ ...S.propMetaChip, background: "#E8622A18", color: "#E8622A" }}>${paid.toLocaleString()} paid</div>}
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {c.email && (
                      <button
                        style={{ ...S.btnSmall, background: "#22AA5518", color: "#22AA55", border: "1px solid #22AA5544", flex: 1 }}
                        onClick={() => handleETransfer(c)}
                      >
                        💸 e-Transfer
                      </button>
                    )}
                    {c.email && !c.stripeOnboarded && (
                      <button
                        style={{ ...S.btnSmall, background: "#635BFF18", color: "#635BFF", border: "1px solid #635BFF44", flex: 1 }}
                        onClick={() => handleStripeOnboard(c)}
                      >
                        Invite to Stripe
                      </button>
                    )}
                    {c.stripeOnboarded && (
                      <button
                        style={{ ...S.btnSmall, background: "#635BFF18", color: "#635BFF", border: "1px solid #635BFF44", flex: 1 }}
                        onClick={() => handleStripeOnboard(c)}
                      >
                        Stripe Onboarded
                      </button>
                    )}
                    {c.email && (
                      <button
                        style={{ ...S.btnSmall, background: "#ffffff0a", color: copied === c.id ? "#22AA55" : "#9993A8", border: "1px solid #ffffff10" }}
                        onClick={() => handleCopyEmail(c.email, c.id)}
                        title="Copy email"
                      >
                        {copied === c.id ? "✓" : "📋"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
      }

      {/* Add contractor drawer */}
      {showForm && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setShowForm(false)}>
          <div style={S.drawer}>
            <div style={S.drawerTitle}>Add Contractor</div>
            <div style={S.drawerSub}>Add a contractor to your network for ticket assignment and e-Transfer payments.</div>

            <div style={{ fontSize: 12, fontWeight: 700, color: "#5A5570", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Contact Info</div>
            <Field label="Full Name *">
              <input style={S.input} placeholder="e.g. Mike Thompson" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </Field>
            <Field label="Business Name (if incorporated)">
              <input style={S.input} placeholder="e.g. Thompson Plumbing Ltd." value={form.businessName} onChange={(e) => set("businessName", e.target.value)} />
            </Field>
            <Field label="Trade / Specialty">
              <select style={S.select} value={form.trade} onChange={(e) => set("trade", e.target.value)}>
                {TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="Email (for e-Transfer)">
                <input style={S.input} placeholder="contractor@example.com" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </Field>
              <Field label="Phone">
                <input style={S.input} placeholder="604-555-0000" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </Field>
            </div>

            <div style={{ fontSize: 12, fontWeight: 700, color: "#5A5570", marginTop: 16, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Tax Info</div>
            <Field label="GST Number">
              <input style={S.input} placeholder="e.g. 123456789 RT0001" value={form.gstNumber} onChange={(e) => set("gstNumber", e.target.value)} />
            </Field>

            <div style={{ fontSize: 12, fontWeight: 700, color: "#5A5570", marginTop: 16, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Business Address</div>
            <Field label="Street Address">
              <input style={S.input} placeholder="e.g. 123 Main St" value={form.address} onChange={(e) => set("address", e.target.value)} />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="City">
                <input style={S.input} placeholder="Vancouver" value={form.city} onChange={(e) => set("city", e.target.value)} />
              </Field>
              <Field label="Province">
                <select style={S.select} value={form.province} onChange={(e) => set("province", e.target.value)}>
                  {["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Postal Code">
                <input style={S.input} placeholder="V5P 1A1" value={form.postalCode} onChange={(e) => set("postalCode", e.target.value.toUpperCase())} />
              </Field>
            </div>
            <Field label="Notes">
              <input style={S.input} placeholder="e.g. Preferred for emergency calls" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </Field>

            <div style={{ fontSize: 12, fontWeight: 700, color: "#5A5570", marginTop: 16, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Payment Method</div>
            <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
              {[["etransfer", "💸 e-Transfer", "#22AA55"], ["stripe", "Stripe Connect", "#635BFF"]].map(([val, label, color]) => (
                <button key={val}
                  style={{ flex: 1, padding: "10px", borderRadius: 8, border: `1px solid ${form.paymentMethod === val ? color : "#ffffff15"}`, background: form.paymentMethod === val ? `${color}18` : "transparent", color: form.paymentMethod === val ? color : "#9993A8", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                  onClick={() => set("paymentMethod", val)}>
                  {label}
                </button>
              ))}
            </div>
            {form.paymentMethod === "etransfer" && (
              <div style={{ ...S.infoBanner, marginBottom: 8 }}>
                <span>💸</span>
                <span style={{ fontSize: 12 }}>Contractor will be paid via Interac e-Transfer to their email. Free and instant for Canadian accounts.</span>
              </div>
            )}
            {form.paymentMethod === "stripe" && (
              <div style={{ ...S.infoBanner, marginBottom: 8 }}>
                <span style={{ color: "#635BFF" }}>S</span>
                <span style={{ fontSize: 12 }}>Contractor will receive a Stripe Connect invite to set up direct deposit. They need to complete onboarding before receiving payouts.</span>
              </div>
            )}
            <div style={{ ...S.infoBanner, marginTop: 4 }}>
              <span>📋</span>
              <span style={{ fontSize: 12 }}>GST number is used when generating invoices and for your tax filing records.</span>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button style={{ ...S.btnPrimary, flex: 1 }} onClick={handleAdd}>Add Contractor</button>
              <button style={{ ...S.btnSmall, background: "#ffffff10", color: "#F0EDE8" }} onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorsTab;
